import { SECTOR_MAPPING } from '../constants/investment';

export const getCategoryColor = (category) => {
  const defaultColors = {
    'US Stock': '#3b82f6', // blue-500
    'TH Stock': '#ec4899', // pink-500
    Crypto: '#8b5cf6',     // violet-500 (web3 vibe, distinct from gold)
    ETF: '#10b981',        // emerald-500
    Gold: '#fbbf24',       // amber-400 (yellow gold)
    Cash: '#14b8a6',       // teal-500 (cash balance)
  };

  if (defaultColors[category]) return defaultColors[category];

  let hash = 0;
  for (let i = 0; i < category.length; i += 1) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = (hash & 0x00ffffff).toString(16).toUpperCase();
  return `#${'00000'.substring(0, 6 - color.length)}${color}`;
};

export const getSector = (symbol) => {
  for (const [sector, symbols] of Object.entries(SECTOR_MAPPING)) {
    if (symbols.includes(symbol.toUpperCase())) {
      return sector;
    }
  }
  return 'Other';
};

export const getSectorColor = (sector) => {
  const colors = {
    Technology: '#3b82f6',
    Finance: '#10b981',
    Healthcare: '#f59e0b',
    Consumer: '#ef4444',
    Energy: '#f97316',
    Industrial: '#8b5cf6',
    Materials: '#ec4899',
    Utilities: '#14b8a6',
    'Real Estate': '#f59e0b',
    Communication: '#6366f1',
    Crypto: '#f59e0b',
    Gold: '#fbbf24',
    ETF: '#10b981',
    Other: '#94a3b8',
  };
  return colors[sector] || '#94a3b8';
};

export const calculateHoldings = (transactions, currentPrices, exchangeRate = 33, costBasisMethod = 'FIFO') => {
  const portfolio = {};
  
  // ตรวจสอบว่ามี SELL transactions ที่มี BUY ที่เกี่ยวข้องหรือไม่
  const hasBuyForSymbol = {};
  transactions.forEach(t => {
    if (t.type === 'BUY') {
      hasBuyForSymbol[t.symbol] = true;
    }
  });
  
  const sortedTransactions = [...transactions].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();

    if (dateA !== dateB) {
      if (Number.isNaN(dateA) && !Number.isNaN(dateB)) return 1;
      if (!Number.isNaN(dateA) && Number.isNaN(dateB)) return -1;
      return dateA - dateB;
    }

    if (a.createdAt && b.createdAt && a.createdAt !== b.createdAt) {
      return a.createdAt - b.createdAt;
    }

    if (a.type === 'BUY' && b.type !== 'BUY') return -1;
    if (a.type !== 'BUY' && b.type === 'BUY') return 1;
    return 0;
  });

  sortedTransactions.forEach((transaction) => {
    if (!portfolio[transaction.symbol]) {
      portfolio[transaction.symbol] = {
        symbol: transaction.symbol,
        // Normalize: 'GOLD' → 'Gold' to prevent duplicate categories
        assetClass: (transaction.assetClass && transaction.assetClass.toUpperCase() === 'GOLD') ? 'Gold' : transaction.assetClass,
        currency: transaction.currency || 'USD',
        quantity: 0,
        totalCostUSD: 0,
        realizedPnLUSD: 0,
        totalDividendUSD: 0,
        buyLots: [],
      };
    }

    const item = portfolio[transaction.symbol];
    const feeTax = parseFloat(transaction.feeTax || 0);

    if (transaction.type === 'BUY') {
      item.quantity += transaction.quantity;
      item.totalCostUSD += transaction.price * transaction.quantity;
      const feePerShare = transaction.quantity > 0 ? feeTax / transaction.quantity : 0;
      item.buyLots.push({ price: transaction.price, quantity: transaction.quantity, feePerShare });
      return;
    }

    if (transaction.type === 'SELL') {
      // ข้ามถ้าไม่มี BUY transactions สำหรับ symbol นี้
      if (!hasBuyForSymbol[transaction.symbol]) {
        return;
      }

      // Determine cost method:
      // If user selected 'AVG' (Average Cost), force Average Cost for all assets.
      // Otherwise (FIFO), Crypto & Gold use Average Cost, while Stocks & ETFs use FIFO.
      const ac = (item.assetClass || '').toLowerCase();
      const isCryptoOrGold = ac === 'crypto' || ac === 'gold';
      const useAvgCost = costBasisMethod === 'AVG' || isCryptoOrGold;

      if (useAvgCost) {
        // ── Average Cost Method ──────────────────────────────────────────
        const qtySold = Math.min(transaction.quantity, item.quantity);
        if (qtySold <= 1e-8) return;

        const avgCostPerUnit = item.quantity > 0 ? item.totalCostUSD / item.quantity : 0;
        const totalFeePerUnit = item.buyLots.length > 0
          ? item.buyLots.reduce((sum, lot) => sum + lot.feePerShare * lot.quantity, 0) / item.quantity
          : 0;
        const costOfSold = avgCostPerUnit * qtySold;
        const feeOfSold = totalFeePerUnit * qtySold;
        const sellRevenue = (transaction.price * qtySold) - (feeTax * (qtySold / transaction.quantity));

        item.realizedPnLUSD += sellRevenue - costOfSold - feeOfSold;
        item.quantity -= qtySold;
        if (item.quantity < 1e-8) item.quantity = 0;
        item.totalCostUSD = item.quantity > 0 ? avgCostPerUnit * item.quantity : 0;

        // Proportionally reduce buyLots for consistency
        if (item.quantity > 0) {
          const ratio = item.quantity / (item.quantity + qtySold);
          item.buyLots.forEach(lot => { lot.quantity *= ratio; });
          item.buyLots = item.buyLots.filter(lot => lot.quantity > 1e-8);
        } else {
          item.buyLots = [];
        }
      } else {
        // ── FIFO Method (Stocks & ETFs) ──────────────────────────────────
        let qtyToSell = transaction.quantity;
        let pureCostOfSoldItems = 0;
        let feeOfSoldItems = 0;
        let qtySold = 0;

        while (qtyToSell > 1e-8 && item.buyLots.length > 0) {
          const oldestLot = item.buyLots[0];
          if (oldestLot.quantity <= qtyToSell + 1e-8) {
            pureCostOfSoldItems += oldestLot.price * oldestLot.quantity;
            feeOfSoldItems += oldestLot.feePerShare * oldestLot.quantity;
            qtySold += oldestLot.quantity;
            qtyToSell -= oldestLot.quantity;
            item.buyLots.shift();
          } else {
            pureCostOfSoldItems += oldestLot.price * qtyToSell;
            feeOfSoldItems += oldestLot.feePerShare * qtyToSell;
            qtySold += qtyToSell;
            oldestLot.quantity -= qtyToSell;
            qtyToSell = 0;
          }
        }

        // คำนวณ realized PnL เฉพาะส่วนที่ match กับ BUY lots เท่านั้น
        if (qtySold > 0) {
          item.quantity -= qtySold;
          if (item.quantity < 1e-8) item.quantity = 0;
          item.totalCostUSD = item.buyLots.reduce((sum, lot) => sum + lot.price * lot.quantity, 0);
          const sellRevenue = (transaction.price * qtySold) - (feeTax * (qtySold / transaction.quantity));
          item.realizedPnLUSD += sellRevenue - pureCostOfSoldItems - feeOfSoldItems;
        }
      }
      return;
    }

    if (transaction.type === 'DIVIDEND') {
      const grossDiv = transaction.price * transaction.quantity;
      item.totalDividendUSD += grossDiv - feeTax;
    }
  });

  return Object.values(portfolio)
    .filter((holding) => holding.quantity > 0.00001 || holding.realizedPnLUSD !== 0 || holding.totalDividendUSD !== 0)
    .map((holding) => {
      // For THB-denominated assets (GOLDTHB), convert all values to USD
      const isTHB = holding.currency === 'THB';
      const fxRate = (isTHB && exchangeRate > 0) ? exchangeRate : 1;

      const avgCostNative = holding.quantity > 0 ? holding.totalCostUSD / holding.quantity : 0;
      let marketPriceNative = currentPrices[holding.symbol] !== undefined ? Number(currentPrices[holding.symbol]) : avgCostNative;
      if (isNaN(marketPriceNative) || marketPriceNative <= 0) {
        marketPriceNative = avgCostNative;
      }
      const currentValueNative = holding.quantity * marketPriceNative;
      const unrealizedPnLNative = holding.quantity > 0 ? currentValueNative - holding.totalCostUSD : 0;
      const pnlPercent = holding.totalCostUSD > 0 ? (unrealizedPnLNative / holding.totalCostUSD) * 100 : 0;

      return {
        ...holding,
        // Convert to USD for consistent display
        avgCostUSD: avgCostNative / fxRate,
        currentPriceUSD: marketPriceNative / fxRate,
        currentValueUSD: currentValueNative / fxRate,
        unrealizedPnLUSD: unrealizedPnLNative / fxRate,
        totalCostUSD: holding.totalCostUSD / fxRate,
        realizedPnLUSD: holding.realizedPnLUSD / fxRate,
        totalDividendUSD: holding.totalDividendUSD / fxRate,
        pnlPercent,
        // Keep original THB values for display when user switches to THB mode
        avgCostNative,
        currentPriceNative: marketPriceNative,
        currentValueNative,
        unrealizedPnLNative,
      };
    });
};

export const calculateSummary = (holdings, cashBalanceUSD = 0) => {
  let totalValue = 0;
  let totalCost = 0;
  let totalUnrealizedPnL = 0;
  let totalRealizedPnL = 0;
  let totalDividend = 0;
  let dailyChange = 0;
  const allocation = {};
  const pnlByClass = {};
  const costByClass = {};
  const sectorAllocation = {};
  let totalDividendYield = 0;
  let dividendYieldCount = 0;

  holdings.forEach((holding) => {
    // All values are already in USD (calculateHoldings handles THB→USD conversion)
    totalRealizedPnL += holding.realizedPnLUSD;
    totalDividend += holding.totalDividendUSD;

    if (holding.quantity > 0) {
      totalValue += holding.currentValueUSD;
      totalCost += holding.totalCostUSD;
      totalUnrealizedPnL += holding.unrealizedPnLUSD;
      dailyChange += (holding.unrealizedPnLUSD * 0.02);

      if (allocation[holding.assetClass] === undefined) {
        allocation[holding.assetClass] = 0;
        pnlByClass[holding.assetClass] = 0;
        costByClass[holding.assetClass] = 0;
      }

      allocation[holding.assetClass] += holding.currentValueUSD;
      pnlByClass[holding.assetClass] += holding.unrealizedPnLUSD;
      costByClass[holding.assetClass] += holding.totalCostUSD;

      const sector = getSector(holding.symbol);
      if (sectorAllocation[sector] === undefined) {
        sectorAllocation[sector] = 0;
      }
      sectorAllocation[sector] += holding.currentValueUSD;

      if (holding.totalDividendUSD > 0 && holding.currentValueUSD > 0) {
        totalDividendYield += (holding.totalDividendUSD / holding.currentValueUSD) * 100;
        dividendYieldCount += 1;
      }
    }
  });

  // totalValue and totalCost remain strictly for invested assets
  const totalWealth = totalValue + (cashBalanceUSD > 0 ? cashBalanceUSD : 0);

  if (cashBalanceUSD > 0) {
    allocation['Cash'] = (allocation['Cash'] || 0) + cashBalanceUSD;
    pnlByClass['Cash'] = (pnlByClass['Cash'] || 0);
    costByClass['Cash'] = (costByClass['Cash'] || 0) + cashBalanceUSD; // Cost basis for cash is its value
  }

  const chartData = Object.keys(allocation)
    .filter((key) => allocation[key] > 0)
    .map((key) => ({ name: key, value: allocation[key], percent: totalWealth > 0 ? (allocation[key] / totalWealth) * 100 : 0 }));

  const sectorChartData = Object.keys(sectorAllocation)
    .filter((key) => sectorAllocation[key] > 0)
    .map((key) => ({ name: key, value: sectorAllocation[key], percent: totalValue > 0 ? (sectorAllocation[key] / totalValue) * 100 : 0 }));

  const portfolioVolatility = Math.sqrt(holdings.reduce((sum, holding) => {
    const weight = totalValue > 0 ? holding.currentValueUSD / totalValue : 0;
    const volatility = holding.pnlPercent || 0;
    return sum + (weight * weight * volatility * volatility);
  }, 0)) * 100;

  const sharpeRatio = totalUnrealizedPnL > 0 ? (totalUnrealizedPnL / totalCost) / portfolioVolatility : 0;
  const maxDrawdown = holdings.reduce((max, holding) => {
    const drawdown = holding.totalCostUSD > 0 ? ((holding.totalCostUSD - holding.currentValueUSD) / holding.totalCostUSD) * 100 : 0;
    return Math.max(max, drawdown);
  }, 0);

  const avgDividendYield = dividendYieldCount > 0 ? totalDividendYield / dividendYieldCount : 0;

  const dailyChangePercent = totalValue > 0 ? (dailyChange / totalValue) * 100 : 0;

  return {
    totalValue,
    totalWealth,
    totalCost,
    totalUnrealizedPnL,
    totalRealizedPnL,
    totalDividend,
    dailyChange,
    dailyChangePercent,
    chartData,
    pnlByClass,
    costByClass,
    allocation,
    sectorChartData,
    sectorAllocation,
    portfolioVolatility,
    sharpeRatio,
    maxDrawdown,
    avgDividendYield,
  };
};

/**
 * Compute per-symbol allocation alerts against user-set maximum weight caps.
 *
 * Base = invested assets only (Σ currentValueUSD of holdings with quantity > 0),
 * EXCLUDING cash — matching the per-asset donut in Analytics.
 *
 * Rebalance math: selling reduces BOTH the holding value and the invested base
 * (proceeds become cash, leaving the base). To bring weight w = V/T down to the
 * target fraction t, sell amount:
 *     x = (V - t·T) / (1 - t)
 * shares = x / currentPrice.
 *
 * @param {Array}  holdings  output of calculateHoldings (needs symbol, quantity, currentValueUSD, currentPriceUSD)
 * @param {Object} limits    { SYMBOL: maxPercent } e.g. { NVDA: 20 } — percent units
 * @returns {{ totalBase:number, bySymbol:Object, overweight:Array }}
 */
export const computeAllocationAlerts = (holdings = [], limits = {}) => {
  const active = (holdings || []).filter(h => h.quantity > 0 && (h.currentValueUSD || 0) > 0);
  const totalBase = active.reduce((sum, h) => sum + (h.currentValueUSD || 0), 0);
  const bySymbol = {};
  const overweight = [];

  if (totalBase <= 0) return { totalBase: 0, bySymbol, overweight };

  active.forEach((h) => {
    const limitPct = Number(limits[h.symbol]);
    if (!(limitPct > 0) || limitPct >= 100) return; // no valid cap set for this symbol

    const value = h.currentValueUSD || 0;
    const price = h.currentPriceUSD || 0;
    const weightPct = (value / totalBase) * 100;
    const t = limitPct / 100;
    const isOver = weightPct > limitPct + 1e-9;

    let sellUSD = 0;
    let sellShares = 0;
    if (isOver && t < 1) {
      sellUSD = (value - t * totalBase) / (1 - t);
      if (sellUSD < 0) sellUSD = 0;
      sellShares = price > 0 ? sellUSD / price : 0;
    }

    const entry = {
      symbol: h.symbol,
      value,
      price,
      weightPct,
      limitPct,
      overPct: weightPct - limitPct,
      isOver,
      sellUSD,
      sellShares,
    };
    bySymbol[h.symbol] = entry;
    if (isOver) overweight.push(entry);
  });

  overweight.sort((a, b) => b.overPct - a.overPct);
  return { totalBase, bySymbol, overweight };
};
